/**
 * Plugin Marketplace
 * Browse, download, and share audio plugins
 * Supports: Third-party web plugins, User-uploaded presets, Premium plugin packs
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Store,
  Search,
  Download,
  Upload,
  Star,
  Crown,
  X,
  Loader2,
  Filter,
  Heart,
  Share2,
  CheckCircle2,
  Clock,
  TrendingUp,
  Plug,
  Music2,
  Activity,
  Settings,
  Wand2,
  Package,
  User,
  MessageSquare,
  ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import PresetReviews from '../marketplace/PresetReviews';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plugin type icons
const TYPE_ICONS = {
  effect: Settings,
  instrument: Music2,
  analyzer: Activity,
  utility: Settings,
  midi: Wand2,
};

// Marketplace plugin card
const MarketplacePluginCard = ({ plugin, onInstall, onPreview, installed }) => {
  const TypeIcon = TYPE_ICONS[plugin.type] || Plug;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/50 rounded-xl border border-zinc-700 hover:border-violet-500/50 transition-all overflow-hidden group"
    >
      {/* Plugin header with icon */}
      <div className="relative h-32 bg-gradient-to-br from-violet-600/20 to-indigo-600/20 flex items-center justify-center">
        <TypeIcon className="w-12 h-12 text-violet-400/50" />
        {plugin.premium && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-amber-500/90 rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3 text-white" />
            <span className="text-xs font-bold text-white">PRO</span>
          </div>
        )}
        {plugin.trending && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500/90 rounded-full flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-white" />
            <span className="text-xs font-bold text-white">Trending</span>
          </div>
        )}
      </div>
      
      {/* Plugin info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-white">{plugin.name}</h3>
            <p className="text-xs text-zinc-500 capitalize">{plugin.category} • by {plugin.author}</p>
          </div>
          <div className="flex items-center gap-1 text-amber-400">
            <Star className="w-3 h-3 fill-current" />
            <span className="text-xs">{plugin.rating?.toFixed(1) || 'N/A'}</span>
          </div>
        </div>
        
        <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{plugin.description}</p>
        
        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
          <span className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            {plugin.downloads?.toLocaleString() || 0}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            v{plugin.version || '1.0.0'}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {installed ? (
            <Button
              variant="outline"
              size="sm"
              disabled
              className="flex-1 border-emerald-500/30 text-emerald-400"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Installed
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => onInstall(plugin)}
              className="flex-1 bg-violet-500 hover:bg-violet-600"
            >
              <Download className="w-4 h-4 mr-1" />
              {plugin.price > 0 ? `$${plugin.price}` : 'Install Free'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPreview(plugin)}
            className="text-zinc-400 hover:text-white"
          >
            <Plug className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// User preset card
const UserPresetCard = ({ preset, onDownload, onRate, onViewDetails }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-indigo-500/50 transition-all"
  >
    <div className="flex items-start justify-between mb-2">
      <div>
        <h4 className="text-sm font-medium text-white">{preset.name}</h4>
        <p className="text-xs text-zinc-500">
          for {preset.plugin_name} • by {preset.author}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onRate(preset.id, star)}
            className={`${
              star <= (preset.rating || 0)
                ? 'text-amber-400'
                : 'text-zinc-600'
            }`}
          >
            <Star className="w-3 h-3 fill-current" />
          </button>
        ))}
      </div>
    </div>
    
    {preset.description && (
      <p className="text-xs text-zinc-400 mb-2 line-clamp-2">{preset.description}</p>
    )}
    
    <div className="flex items-center justify-between">
      <span className="text-xs text-zinc-500">
        <Download className="w-3 h-3 inline mr-1" />
        {preset.downloads || 0} downloads
        {preset.reviewCount > 0 && (
          <span className="ml-2">
            <MessageSquare className="w-3 h-3 inline mr-1" />
            {preset.reviewCount} reviews
          </span>
        )}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetails(preset)}
          className="text-zinc-400 hover:text-white text-xs"
        >
          Reviews
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDownload(preset)}
          className="text-indigo-400 hover:text-indigo-300"
      >
        <Download className="w-4 h-4 mr-1" />
        Use
      </Button>
      </div>
    </div>
  </motion.div>
);

// Preset Detail View with Reviews
const PresetDetailView = ({ preset, onBack, onDownload, currentUserId }) => (
  <div className="h-full flex flex-col">
    {/* Header */}
    <div className="p-4 border-b border-zinc-700 flex items-center gap-3">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="text-zinc-400 hover:text-white"
      >
        <ChevronLeft className="w-5 h-5" />
      </Button>
      <div className="flex-1">
        <h3 className="font-semibold text-white">{preset.name}</h3>
        <p className="text-xs text-zinc-500">by {preset.author}</p>
      </div>
      <Button
        onClick={() => onDownload(preset)}
        className="bg-indigo-500 hover:bg-indigo-600"
      >
        <Download className="w-4 h-4 mr-2" />
        Download
      </Button>
    </div>
    
    {/* Content */}
    <div className="flex-1 overflow-y-auto p-4">
      {/* Preset info */}
      <div className="mb-6 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= (preset.rating || 0)
                    ? 'fill-amber-400 text-amber-400'
                    : 'fill-zinc-600 text-zinc-600'
                }`}
              />
            ))}
            <span className="text-sm text-zinc-400 ml-2">
              ({preset.ratingCount || 0} ratings)
            </span>
          </div>
          <span className="text-sm text-zinc-500">
            <Download className="w-4 h-4 inline mr-1" />
            {preset.downloads || 0} downloads
          </span>
        </div>
        {preset.description && (
          <p className="text-zinc-400">{preset.description}</p>
        )}
        {preset.tags && preset.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {preset.tags.map((tag, i) => (
              <span key={i} className="px-2 py-1 bg-zinc-700 rounded text-xs text-zinc-300">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Reviews section */}
      <PresetReviews
        presetId={preset.id}
        presetName={preset.name}
        currentUserId={currentUserId}
      />
    </div>
  </div>
);

export const PluginMarketplace = ({
  isOpen,
  onClose,
  onInstallPlugin,
  installedPlugins = [],
  currentUserId = null,
}) => {
  const [activeTab, setActiveTab] = useState('discover'); // discover, presets, upload
  const [plugins, setPlugins] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [sortBy, setSortBy] = useState('popular'); // popular, newest, rating
  
  // Load marketplace plugins
  useEffect(() => {
    if (isOpen && activeTab === 'discover') {
      loadMarketplacePlugins();
    } else if (isOpen && activeTab === 'presets') {
      loadCommunityPresets();
    }
  }, [isOpen, activeTab, selectedType, sortBy]);

  const loadMarketplacePlugins = async () => {
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/plugins/marketplace?sort=${sortBy}`;
      if (selectedType) url += `&type=${selectedType}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setPlugins(data.plugins || []);
    } catch (err) {
      console.error('Error loading marketplace:', err);
      // Use fallback data
      setPlugins(getDefaultMarketplacePlugins());
    } finally {
      setIsLoading(false);
    }
  };

  const loadCommunityPresets = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/plugins/presets/community?sort=${sortBy}`);
      const data = await response.json();
      setPresets(data.presets || []);
    } catch (err) {
      console.error('Error loading presets:', err);
      setPresets([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fallback marketplace plugins
  const getDefaultMarketplacePlugins = () => [
    {
      id: 'mp-multiband-comp',
      name: 'Multiband Compressor Pro',
      type: 'effect',
      category: 'dynamics',
      author: 'String Sauce',
      description: 'Professional 4-band compressor with per-band controls, sidechain, and visual feedback',
      version: '2.0.0',
      rating: 4.8,
      downloads: 12500,
      price: 0,
      premium: false,
      trending: true,
    },
    {
      id: 'mp-vintage-eq',
      name: 'Vintage Console EQ',
      type: 'effect',
      category: 'eq',
      author: 'String Sauce',
      description: 'Classic analog-modeled EQ with tube saturation and harmonic enhancement',
      version: '1.5.0',
      rating: 4.9,
      downloads: 8700,
      price: 4.99,
      premium: true,
    },
    {
      id: 'mp-synth-engine',
      name: 'Poly Synth Engine',
      type: 'instrument',
      category: 'synthesizer',
      author: 'String Sauce',
      description: 'Versatile polyphonic synthesizer with 3 oscillators, filters, and modulation matrix',
      version: '1.0.0',
      rating: 4.7,
      downloads: 5600,
      price: 9.99,
      premium: true,
      trending: true,
    },
    {
      id: 'mp-tape-saturator',
      name: 'Tape Saturator',
      type: 'effect',
      category: 'distortion',
      author: 'String Sauce',
      description: 'Warm tape saturation with vintage character and hiss simulation',
      version: '1.2.0',
      rating: 4.6,
      downloads: 3200,
      price: 0,
      premium: false,
    },
    {
      id: 'mp-stereo-widener',
      name: 'Stereo Widener',
      type: 'effect',
      category: 'utility',
      author: 'String Sauce',
      description: 'Add width and depth to your mix with mid/side processing',
      version: '1.0.0',
      rating: 4.5,
      downloads: 7800,
      price: 0,
      premium: false,
    },
    {
      id: 'mp-gate-expander',
      name: 'Gate & Expander',
      type: 'effect',
      category: 'dynamics',
      author: 'String Sauce',
      description: 'Clean up your tracks with precise gating and smooth expansion',
      version: '1.1.0',
      rating: 4.4,
      downloads: 2100,
      price: 0,
      premium: false,
    },
  ];

  const handleInstallPlugin = async (plugin) => {
    if (plugin.price > 0 && !plugin.purchased) {
      toast.error('Premium plugins require purchase');
      return;
    }
    
    try {
      await onInstallPlugin?.(plugin);
      toast.success(`${plugin.name} installed!`);
    } catch (err) {
      toast.error('Failed to install plugin');
    }
  };

  const handlePreviewPlugin = (plugin) => {
    toast.info(`Previewing ${plugin.name}...`);
  };

  const handleDownloadPreset = async (preset) => {
    try {
      const response = await fetch(`${API_URL}/api/plugins/presets/${preset.id}/download`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Download failed');
      toast.success(`Preset "${preset.name}" added!`);
    } catch (err) {
      toast.error('Failed to download preset');
    }
  };

  const handleRatePreset = async (presetId, rating) => {
    try {
      await fetch(`${API_URL}/api/plugins/presets/${presetId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating }),
      });
      // Update local state
      setPresets(prev => prev.map(p =>
        p.id === presetId ? { ...p, rating } : p
      ));
    } catch (err) {
      toast.error('Failed to rate preset');
    }
  };

  const filteredPlugins = plugins.filter(plugin => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return plugin.name.toLowerCase().includes(query) ||
             plugin.description?.toLowerCase().includes(query) ||
             plugin.category.toLowerCase().includes(query);
    }
    return true;
  });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-5xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="plugin-marketplace"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Plugin Marketplace</h2>
                <p className="text-xs text-zinc-400">
                  Discover, download, and share plugins
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex items-center gap-1 p-3 border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('discover')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'discover'
                  ? 'bg-violet-500/20 text-violet-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              <Store className="w-4 h-4 inline mr-2" />
              Discover
            </button>
            <button
              onClick={() => setActiveTab('presets')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'presets'
                  ? 'bg-violet-500/20 text-violet-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Community Presets
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'upload'
                  ? 'bg-violet-500/20 text-violet-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {/* Sidebar filters */}
            <div className="w-48 border-r border-zinc-800 p-3 space-y-4 hidden md:block">
              <div>
                <h4 className="text-xs font-medium text-zinc-400 uppercase mb-2">Type</h4>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedType(null)}
                    className={`w-full px-3 py-2 rounded-lg text-left text-sm ${
                      !selectedType ? 'bg-violet-500/20 text-violet-400' : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    All Types
                  </button>
                  {['effect', 'instrument', 'analyzer'].map(type => {
                    const Icon = TYPE_ICONS[type];
                    return (
                      <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2 ${
                          selectedType === type ? 'bg-violet-500/20 text-violet-400' : 'text-zinc-400 hover:bg-zinc-800'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="capitalize">{type}s</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <h4 className="text-xs font-medium text-zinc-400 uppercase mb-2">Sort By</h4>
                <div className="space-y-1">
                  {[
                    { id: 'popular', label: 'Most Popular', icon: TrendingUp },
                    { id: 'newest', label: 'Newest', icon: Clock },
                    { id: 'rating', label: 'Highest Rated', icon: Star },
                  ].map(sort => (
                    <button
                      key={sort.id}
                      onClick={() => setSortBy(sort.id)}
                      className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2 ${
                        sortBy === sort.id ? 'bg-violet-500/20 text-violet-400' : 'text-zinc-400 hover:bg-zinc-800'
                      }`}
                    >
                      <sort.icon className="w-4 h-4" />
                      {sort.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Main content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search */}
              <div className="p-3 border-b border-zinc-800">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={activeTab === 'discover' ? 'Search plugins...' : 'Search presets...'}
                    className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:border-violet-500 focus:outline-none"
                  />
                </div>
              </div>
              
              {/* Results */}
              <div className="flex-1 overflow-y-auto p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                  </div>
                ) : activeTab === 'discover' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPlugins.map(plugin => (
                      <MarketplacePluginCard
                        key={plugin.id}
                        plugin={plugin}
                        installed={installedPlugins.includes(plugin.id)}
                        onInstall={handleInstallPlugin}
                        onPreview={handlePreviewPlugin}
                      />
                    ))}
                    {filteredPlugins.length === 0 && (
                      <div className="col-span-full text-center py-12">
                        <Plug className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-500">No plugins found</p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'presets' ? (
                  selectedPreset ? (
                    <PresetDetailView
                      preset={selectedPreset}
                      onBack={() => setSelectedPreset(null)}
                      onDownload={handleDownloadPreset}
                      currentUserId={currentUserId}
                    />
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {presets.length > 0 ? (
                      presets.map(preset => (
                        <UserPresetCard
                          key={preset.id}
                          preset={preset}
                          onDownload={handleDownloadPreset}
                          onRate={handleRatePreset}
                          onViewDetails={setSelectedPreset}
                        />
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-500">No community presets yet</p>
                        <p className="text-xs text-zinc-600 mt-1">Be the first to share!</p>
                      </div>
                    )}
                  </div>
                  )
                ) : (
                  /* Upload tab */
                  <div className="max-w-md mx-auto py-8">
                    <div className="text-center mb-6">
                      <Upload className="w-12 h-12 text-violet-400 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-2">Share Your Presets</h3>
                      <p className="text-sm text-zinc-400">
                        Upload your custom plugin presets for the community
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Preset Name</label>
                        <input
                          type="text"
                          placeholder="My Awesome Preset"
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">For Plugin</label>
                        <select className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                          <option>Studio Reverb</option>
                          <option>Tape Delay</option>
                          <option>Dynamic Compressor</option>
                          <option>Parametric EQ</option>
                          <option>Vintage Chorus</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Description</label>
                        <textarea
                          placeholder="Describe what makes this preset special..."
                          rows={3}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Tags</label>
                        <input
                          type="text"
                          placeholder="vocal, warm, vintage"
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        />
                      </div>
                      <Button className="w-full bg-violet-500 hover:bg-violet-600">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Preset
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PluginMarketplace;
